/**
 * The pay page — `/billing?pay={operation}` (plan-020 **WAPP-BILL**; design §32.10, §32.17; ED-17, R-APP-4).
 *
 * Every plan mail and the renewal's payment request link HERE, never to the processor's page itself (a hosted page
 * lives 30 minutes; a mail can be opened days later): the page reads the operation (`GET
 * /v1/billing-operations/{id}/payment`), says what it is for and what it costs (net + VAT = total), and "Pay {total}"
 * asks for a FRESH page (`POST …/pay`) and sends the browser there. Coming back from the processor, the same page reads
 * the outcome — it never reports one (FR12-BIL-08): the payment worker applies the capture, and the page re-reads the
 * status while it is open.
 *
 * Paying needs `account.finance.manage` (the API refuses anyone else); a finance reader sees the payment and is told
 * who can pay. An operation of another organization, or one that no longer exists, is "not one we can show you".
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { money, type CurrencyCode } from "@wetalk/domain";
import { Button, Icon, Tag } from "@wetalk/ui";
import type { ReactNode } from "react";

import { apiProblemText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import type { OperationPaymentView, PaymentStatusWordView } from "../../api/slice/billing-operation.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
import { formatMoney } from "../../money.js";
import { GLYPH } from "../../shell/glyph.js";
import { PAYMENT_STATUS_REFRESH_MS, operationPaymentQueryKey, paymentStatusCopyOf } from "../finance/funding/payment-status.js";
import { payDekOf, useSharedSubscription, usePayOperation } from "./plan-billing.js";

/** The statuses after which nothing more happens to the operation. */
const SETTLED: ReadonlySet<PaymentStatusWordView> = new Set<PaymentStatusWordView>(["completed", "failed", "cancelled"]);
/** The statuses in which the person can ask for a (fresh) payment page. */
const PAYABLE: ReadonlySet<PaymentStatusWordView> = new Set<PaymentStatusWordView>(["pay_now", "payment_page_open", "awaiting_payment"]);

/** The status line: the shared payment words, with a plan renewal's own once it is paid. Pure. */
export function payStatusLineOf(view: Pick<OperationPaymentView, "kind" | "status">): { readonly label: string; readonly line: string; readonly tone: "positive" | "caution" | "critical" | "muted" } {
  const shared = paymentStatusCopyOf(view.status, view.kind);
  if (view.kind !== "subscription_renewal") return shared;
  if (view.status === "pay_now") return { ...shared, line: portalText("billing", "pay.waiting") };
  if (view.status === "completed") return { ...shared, line: portalText("billing", "pay.paid") };
  if (view.status === "applying" || view.status === "paid_not_applied") return { ...shared, line: portalText("billing", "pay.applying") };
  return shared;
}

function amountText(minor: number | null, currency: string): string {
  return minor === null ? "—" : formatMoney(money(minor, currency as CurrencyCode));
}

export interface PlanPayPanelProps {
  readonly operationId: string;
  readonly onClose: () => void;
}

export function PlanPayPanel(props: PlanPayPanelProps): ReactNode {
  usePortalTranslation("billing");
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const shared = useSharedSubscription();
  const key = operationPaymentQueryKey(props.operationId);
  const read = useQuery({
    queryKey: key,
    queryFn: () => gateway.readOperationPayment(props.operationId),
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === undefined || SETTLED.has(status) ? false : PAYMENT_STATUS_REFRESH_MS;
    },
  });
  const pay = usePayOperation((view) => {
    queryClient.setQueryData(key, view);
  });

  const head = (
    <div className="bl-card-head">
      <p className="bl-card-title" id="bl-pay-title">{portalText("billing", "pay.title")}</p>
      <span className="bl-flush">
        <Button variant="ghost" onClick={props.onClose}>{portalText("billing", "pay.close")}</Button>
      </span>
    </div>
  );

  if (read.isPending) {
    return (
      <section className="bl-card bl-pay" aria-labelledby="bl-pay-title" aria-busy="true">
        {head}
        <p className="bl-card-line">{portalText("finance", "m0607")}</p>
      </section>
    );
  }
  if (read.isError) {
    const missing = read.error instanceof ApiError ? read.error.status === 404 : read.error instanceof Error && Reflect.get(read.error, "code") === "not_found";
    return (
      <section className="bl-card bl-pay" aria-labelledby="bl-pay-title">
        {head}
        <p className="bl-plan-error" role="alert">
          {missing ? portalText("billing", "pay.not_found") : apiProblemText(read.error, portalText("finance", "m0608"))}
        </p>
        {missing ? null : (
          <div className="bl-actions">
            <Button variant="secondary" onClick={() => void read.refetch()}>{portalText("finance", "m0382")}</Button>
          </div>
        )}
      </section>
    );
  }

  const view = read.data;
  const copy = payStatusLineOf(view);
  const glyph = copy.tone === "positive" ? GLYPH.checkCircle : copy.tone === "critical" ? GLYPH.xCircle : GLYPH.clockCounterClockwise;
  const payable = view.payableNow && PAYABLE.has(view.status);
  return (
    <section className="bl-card bl-pay" aria-labelledby="bl-pay-title">
      {head}
      <p className="bl-card-line">{payDekOf(view, shared.held ?? null)}</p>
      <dl className="bl-pay-stats">
        <div className="bl-pay-stat">
          <dt>{portalText("billing", "pay.net")}</dt>
          <dd>{amountText(view.netMinor, view.currency)}</dd>
        </div>
        <div className="bl-pay-stat">
          <dt>{portalText("billing", "pay.vat")}</dt>
          <dd>{amountText(view.taxMinor, view.currency)}</dd>
        </div>
        <div className="bl-pay-stat">
          <dt>{portalText("billing", "pay.total")}</dt>
          <dd>{amountText(view.amountDueMinor, view.currency)}</dd>
        </div>
      </dl>
      <div className="bl-pay-status" role="status">
        <Icon glyph={glyph} size="xl" tone={copy.tone === "muted" ? "muted" : copy.tone} />
        <span className="bl-pay-status-body">
          <Tag status={copy.tone}>{copy.label}</Tag>
          <span className="bl-card-line">{copy.line}</span>
        </span>
      </div>
      {pay.isError ? (
        <p className="bl-plan-error" role="alert">
          {apiProblemText(pay.error, portalText("finance", "m0615"))}
        </p>
      ) : null}
      {payable && shared.canManage ? (
        <>
          <div className="bl-actions">
            <Button variant="primary" disabled={pay.isPending} onClick={() => pay.mutate(view.operationId)}>
              {portalText("billing", "pay.button", { total: amountText(view.amountDueMinor, view.currency) })}
            </Button>
          </div>
          <p className="bl-card-note">{pay.isPending ? portalText("billing", "pay.opening") : portalText("billing", "pay.page_note")}</p>
        </>
      ) : null}
      {payable && !shared.canManage ? <p className="bl-card-note">{portalText("billing", "pay.read_only")}</p> : null}
    </section>
  );
}
